import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import DeleteIconOutline from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Button,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Draft } from "assets/material-custom-icons";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { OpportunityPdfPreview } from "components/reports/ReportMeasuresTable/Opportunity/OpportunityPdfPreview";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useDidUpdate, useModals } from "hooks";
import {
  IApiServiceError,
  useDealActions,
  usePickUserPermissions,
  usePortfolioConstructionActions,
  useUserRoleSelector
} from "store";
import { UserPermissionType } from "store/types";
import {
  IPortfolioConstructionInvestmentGoal,
  IPortfolioConstructionOpportunity,
  IPortfolioConstructionTableRowData
} from "store/types";
import { toastify } from "utils/tyostify";

import { RoleName } from "../../../constants";
import { EditTargetPopup, EditTargetPopupSubmitParams } from "./EditTargetPopup";

// Styled components for common TableCell styles
const StyledHeaderCell = styled(TableCell)({
  backgroundColor: "#F6F8FC",
  fontWeight: "500",
  padding: "12px 16px",
  fontSize: "16px",
  whiteSpace: "nowrap"
});

const StyledBodyCell = styled(TableCell)({
  padding: "12px 16px",
  fontSize: "16px",
  borderBottom: "1px solid #DBE8F5"
});

const StyledInteractiveCell = styled(TableCell)({
  padding: "12px 16px",
  fontSize: "16px",
  borderBottom: "1px solid #DBE8F5",
  cursor: "pointer"
});

const StyledSpacerCell = styled(TableCell)({
  backgroundColor: "#FFFFFF",
  border: "none",
  borderBottom: "1px solid #DBE8F5",
  padding: "8px 16px",
  textAlign: "center"
});

const StyledActionsCell = styled(TableCell)({
  padding: "12px 16px",
  width: "50px",
  borderBottom: "1px solid #DBE8F5"
});

interface SimpleTableProps {
  investorId: number;
  refetch: () => void;
  isNeedToRefresh: boolean;
  invalidateRefresh: () => void;
  data: IPortfolioConstructionTableRowData[];
  loading: boolean;
  getData: (investorId: number, showEmptyClasses?: boolean) => Promise<IPortfolioConstructionTableRowData[]>;
  setData: React.Dispatch<React.SetStateAction<IPortfolioConstructionTableRowData[] | undefined>>;
  goalsData: IPortfolioConstructionInvestmentGoal | undefined;
  setGoalsData: React.Dispatch<React.SetStateAction<IPortfolioConstructionInvestmentGoal | undefined>>;
}

export type EditingField = {
  row: IPortfolioConstructionTableRowData;
  field: "target" | "target_percent" | "actual_investment" | "actual_percentage" | "expected_return";
  anchorEl: HTMLElement;
};

// Helper type for split data with empty row
type SplitTableData = (IPortfolioConstructionTableRowData | { isSpacer: true; title: string })[];

export const SimpleTable: React.FC<SimpleTableProps> = ({
  investorId,
  refetch,
  isNeedToRefresh,
  data,
  loading,
  getData,
  setData,
  goalsData,
  setGoalsData
}) => {
  const isAdmin = useUserRoleSelector()?.name === RoleName.Admin;
  const featurePermission = usePickUserPermissions("portfolio_construction");
  const isViewerPermission = featurePermission === UserPermissionType.VIEWER;
  const [showEmptyClasses, setShowEmptyClasses] = useState(false);
  const [editingField, setEditingField] = useState<EditingField | null>(null);
  const [rowOpportunity, setRowOpportunity] = useState<IPortfolioConstructionOpportunity[]>([]);
  const [aumAnchorEl, setAumAnchorEl] = useState<HTMLElement | null>(null);
  const [aumInputValue, setAumInputValue] = useState<string>("");

  const { createModal, deleteModal } = useModals();
  const {
    addTargetAction,
    addActualInvestmentAction,
    createAssetClassAction,
    deleteCustomActualInvestmentAction,
    deleteCustomAssetClassAction,
    pinAssetClassAction,
    addExpectedReturnAction,
    createInvestmentGoalsAction
  } = usePortfolioConstructionActions();

  const { action: saveAum, loading: saveAumLoading } = useActionHook(createInvestmentGoalsAction, {
    onSuccess: (res) => {
      setGoalsData(res);
      setAumAnchorEl(null);
      getData(investorId, showEmptyClasses || undefined);
      refetch();
    }
  });

  useEffect(() => {
    if (goalsData?.aum != null) {
      setAumInputValue(goalsData.aum.toString());
    } else {
      setAumInputValue("");
    }
  }, [goalsData?.aum]);

  const formatAum = (value: number | null | undefined): string => {
    if (value == null) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleSaveAum = async () => {
    const numericAum = aumInputValue !== "" ? Number(aumInputValue) : null;
    const g = goalsData;
    const goalsToSave: IPortfolioConstructionInvestmentGoal = {
      aum: numericAum,
      target_return: g && g.target_return != null ? g.target_return : null,
      target_volatility: g && g.target_volatility != null ? g.target_volatility : null,
      max_drawdown: g && g.max_drawdown != null ? g.max_drawdown : null,
      liquid_month_0_3: g && g.liquid_month_0_3 != null ? g.liquid_month_0_3 : null,
      liquid_month_3_12: g && g.liquid_month_3_12 != null ? g.liquid_month_3_12 : null,
      liquid_year_1_3: g && g.liquid_year_1_3 != null ? g.liquid_year_1_3 : null,
      liquid_year_3_10: g && g.liquid_year_3_10 != null ? g.liquid_year_3_10 : null,
      liquid_year_10_plus: g && g.liquid_year_10_plus != null ? g.liquid_year_10_plus : null,
      is_ai: g?.is_ai || false,
      ai_note: g && g.ai_note != null ? g.ai_note : null
    };
    await saveAum(investorId, goalsToSave);
  };

  const { updateAssetClassAccountDataAction } = useDealActions();
  // Helper function to format percentage values
  const formatPercentage = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "-";
    if (value === 0) return "0%";
    return Number.isInteger(value) ? `${value}%` : `${value.toFixed(2)}%`;
  };

  // Helper function to check if row is a spacer
  const isSpacerRow = (row: SplitTableData[0]): row is { isSpacer: true; title: string } => {
    return "isSpacer" in row;
  };

  // Helper function to split data into 2 sections with empty row
  const splitDataWithSpacer = useCallback(
    (originalData: IPortfolioConstructionTableRowData[]): SplitTableData => {
      if (!originalData || originalData.length === 0) return [];

      const firstSection: IPortfolioConstructionTableRowData[] = [];
      const secondSection: IPortfolioConstructionTableRowData[] = [];

      originalData.forEach((item) => {
        if (item.actual_investment && item.actual_investment > 0) {
          firstSection.push(item);
        } else {
          secondSection.push(item);
        }
      });

      // Only add spacer if second section has at least 1 element
      if (secondSection.length > 0) {
        return [
          ...firstSection,
          { isSpacer: true, title: "No Actual Investment" } as { isSpacer: true; title: string },
          ...secondSection
        ];
      }

      return firstSection;
    },
    [showEmptyClasses]
  );

  // Create split data with spacer
  const splitData = useMemo(() => splitDataWithSpacer(data), [data, splitDataWithSpacer]);

  const isActualInvestmentOverAum = useMemo(() => {
    if (goalsData?.aum == null) return false;
    const total = data.reduce((sum, row) => sum + (row.actual_investment ?? 0), 0);
    return total > goalsData.aum;
  }, [data, goalsData?.aum]);

  const { action: addTarget, loading: targetLoading } = useActionHook(addTargetAction, {
    onSuccess: () => {
      setEditingField(null); //close the popup
      getData(investorId, showEmptyClasses || undefined);
      refetch();
    }
  });

  const { action: addActualInvestment, loading: actualInvestmentLoading } = useActionHook(addActualInvestmentAction, {
    onSuccess: async (res) => {
      if (!res) return;
      setEditingField(null); //close the popup
      setData((prev) => {
        if (
          !showEmptyClasses &&
          !res.actual_investment &&
          !res.actual_percentage &&
          !res.target &&
          !res.target_percent
        ) {
          return (
            prev?.filter(
              (item) => item.asset_class_id === res.asset_class_id && item.asset_class_type === res.asset_class_type
            ) || []
          ); // if actual investment && percentage were set as 0, remove the row overwise update the table
        }
        return (
          prev?.map((item) =>
            item.asset_class_id === res.asset_class_id && item.asset_class_type === res.asset_class_type
              ? { ...res }
              : item
          ) || []
        ); //if show empty classes, move row to the empty classes
      });
      refetch(); //refetch charts data
    }
  });

  const { action: createAssetClass, loading: assetClassLoading } = useActionHook(createAssetClassAction, {
    onSuccess: (res) => {
      setData((prev) => (prev ? [...prev, res] : [res]));
    }
  });

  const { action: deleteCustomActualInvestment } = useActionHook(deleteCustomActualInvestmentAction, {
    onSuccess: () => {
      setEditingField(null); //close the popup
      getData(investorId, showEmptyClasses || undefined);
      refetch();
    }
  });

  const { action: deleteCustomAssetClass } = useActionHook(deleteCustomAssetClassAction, {
    onSuccess: (id) => {
      setData((prev) => prev?.filter((item) => item.asset_class_id !== id) || []);
    }
  });

  const { action: updateExpectedReturn, loading: updateExpectedReturnLoading } = useActionHook(
    updateAssetClassAccountDataAction,
    {
      onSuccess: async () => {
        setEditingField(null); //close the popup
        getData(investorId, showEmptyClasses || undefined);
        refetch();
      }
    }
  );

  const { action: addExpectedReturn, loading: addExpectedReturnLoading } = useActionHook(addExpectedReturnAction, {
    onSuccess: async () => {
      setEditingField(null); //close the popup
      getData(investorId, showEmptyClasses || undefined);
      refetch();
    }
  });
  const handleSubmit = async ({ id, field, asset_class_type, value }: EditTargetPopupSubmitParams) => {
    if (field === "actual_investment" || field === "actual_percentage") {
      await addActualInvestment({
        investor_id: investorId,
        asset_class_type,
        asset_class_id: id,
        [field]: value
      });
      return;
    }
    if (field === "expected_return") {
      asset_class_type === "system"
        ? await updateExpectedReturn({
            id,
            expected_return: value
          })
        : await addExpectedReturn({
            investor_id: investorId,
            asset_class_type,
            asset_class_id: id,
            expected_return: value
          });
      return;
    }
    await addTarget({
      investor_id: investorId,
      asset_class_type,
      asset_class_id: id,
      [field]: value
    });
  };

  const onAddAssetClass = useCallback(() => {
    createModal({
      action: async (title: string) => {
        await createAssetClass(title);
      },
      title: "Add New Asset Class",
      submitButtonTitle: "Add"
    });
  }, [createModal]);

  const handlePinAssetClass = useCallback(
    async (data: { asset_class_id: number; asset_class_type: "system" | "custom"; pin: boolean }) => {
      await pinAssetClassAction(data);
      setData(
        (prev) =>
          prev?.map((item) =>
            item.asset_class_id === data.asset_class_id && item.asset_class_type === data.asset_class_type
              ? { ...item, is_pinned: data.pin }
              : item
          ) || []
      );
    },
    [pinAssetClassAction]
  );

  const handleDeleteAssetClass = useCallback(
    async (assetClassId: number) => {
      deleteModal({
        action: async () => {
          try {
            await deleteCustomAssetClass(assetClassId);
          } catch (error) {
            const errorMessage = (error as IApiServiceError).message;
            toastify({ message: errorMessage, type: "error" });
          }
        }
      });
    },
    [deleteModal]
  );

  const investorChangeRef = useRef(false);

  useDidUpdate(() => {
    if (showEmptyClasses) {
      investorChangeRef.current = true;
      setShowEmptyClasses(false);
    }
    // Don't call getData here — parent handles it via useEffect([id])
  }, [investorId]);

  useDidUpdate(() => {
    if (investorChangeRef.current) {
      investorChangeRef.current = false;
      return;
    }
    // Only fetch when showEmptyClasses changes by user action
    if (investorId) {
      getData(investorId, showEmptyClasses);
    }
  }, [showEmptyClasses]);

  useDidUpdate(() => {
    if (isNeedToRefresh) {
      getData(investorId, showEmptyClasses || undefined);
      refetch();
    }
  }, [isNeedToRefresh]);

  const renderDataRow = (row: IPortfolioConstructionTableRowData, index: number) => {
    // Check if this row is in the second section (no actual investment)
    const isSecondSection = !row.actual_investment || row.actual_investment === 0;

    return (
      <TableRow
        key={`data-${index}`}
        sx={{
          backgroundColor: "#FFFFFF !important",
          fontWeight: !row.asset_class_id ? "500" : "400",
          "&:hover .MuiButtonBase-root": {
            opacity: 1
          }
        }}
      >
        <StyledBodyCell
          sx={{
            ...(row.asset_class_id && { paddingLeft: "36px" }),
            fontWeight: !row.asset_class_id ? "500" : "400"
          }}
        >
          <Stack direction="row" alignItems="center" gap="8px">
            {isSecondSection && showEmptyClasses && (
              <MaterialTooltip isShow title={row.is_pinned ? "Include in analysis" : "Exclude from analysis"}>
                <Switch
                  checked={row.is_pinned || false}
                  onChange={(e) => {
                    handlePinAssetClass({
                      asset_class_id: row.asset_class_id,
                      asset_class_type: row.asset_class_type,
                      pin: e.target.checked
                    });
                  }}
                  size="small"
                  sx={{
                    width: 48,
                    height: 24,
                    padding: 0,
                    "& .MuiSwitch-switchBase": {
                      padding: 0,
                      margin: "2px",
                      transitionDuration: "300ms",
                      color: "#bac7ce",
                      "&.Mui-checked": {
                        transform: "translateX(23px)",
                        color: "#0a3142",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "rgba(246, 248, 252, 1)",
                          opacity: 1,
                          border: 0
                        },
                        "&.Mui-disabled + .MuiSwitch-track": {
                          opacity: 0.5
                        }
                      },
                      "&.Mui-focusVisible .MuiSwitch-thumb": {
                        color: "#33cf4d",
                        border: "6px solid #fff"
                      },
                      "&.Mui-disabled .MuiSwitch-thumb": {
                        opacity: 0.7
                      },
                      "&.Mui-disabled + .MuiSwitch-track": {
                        opacity: 0.3
                      }
                    },
                    "& .MuiSwitch-thumb": {
                      boxSizing: "border-box",
                      width: 20,
                      height: 20
                    },
                    "& .MuiSwitch-track": {
                      borderRadius: "34px",
                      backgroundColor: "rgba(246, 248, 252, 1)",
                      opacity: 1,
                      transition: "background-color 500ms"
                    }
                  }}
                />
              </MaterialTooltip>
            )}
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap="4px" sx={{ flex: 1 }}>
              <Typography>{row.asset_class_name}</Typography>
              {!!row.opportunities.length && (
                <Button
                  variant="contained"
                  sx={{ backgroundColor: "#EBAF01", color: "white" }}
                  size="sm"
                  onClick={() => setRowOpportunity(row.opportunities)}
                >
                  Opportunity
                </Button>
              )}
            </Stack>
          </Stack>
        </StyledBodyCell>
        <StyledBodyCell
          sx={{
            cursor: isAdmin && !isViewerPermission ? "pointer" : "default",
            color: isAdmin && !isViewerPermission ? "#26A6AE" : "#071F35"
          }}
          onClick={(e) => {
            if (isAdmin && !isViewerPermission) {
              setEditingField({
                row,
                field: "expected_return",
                anchorEl: e.currentTarget
              });
            }
          }}
        >
          {formatPercentage(row.expected_return)}
        </StyledBodyCell>
        <StyledInteractiveCell
          sx={{
            color: !isViewerPermission && row.asset_class_id ? "#26A6AE" : "#071F35",
            cursor: !isViewerPermission && row.asset_class_id ? "pointer" : "default"
          }}
          onClick={(e) =>
            !isViewerPermission &&
            row.asset_class_id &&
            setEditingField({
              row,
              field: "actual_investment",
              anchorEl: e.currentTarget
            })
          }
        >
          <Stack direction="row" alignItems="center" gap="10px">
            {row.actual_investment !== null && row.actual_investment !== undefined
              ? row.actual_investment.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                  minimumFractionDigits: 0
                })
              : "-"}
            {row.actual_investment_original && <Draft sx={{ fontSize: "16px", fill: "#496682" }} />}
          </Stack>
        </StyledInteractiveCell>
        <StyledBodyCell
          sx={{
            color: "#071F35"
          }}
          // onClick={(e) =>
          //   row.asset_class_id &&
          //   setEditingField({
          //     row,
          //     field: "actual_percentage",
          //     anchorEl: e.currentTarget
          //   })
          // }
        >
          <Stack direction="row" alignItems="center" gap="10px">
            {formatPercentage(row.actual_percentage)}
            {row.actual_percentage_original && <Draft sx={{ fontSize: "16px", fill: "#496682" }} />}
          </Stack>
        </StyledBodyCell>
        <StyledInteractiveCell
          sx={{
            color: !isViewerPermission && row.asset_class_id ? "#26A6AE" : "#071F35",
            cursor: !isViewerPermission && row.asset_class_id ? "pointer" : "default"
          }}
          onClick={(e) =>
            !isViewerPermission &&
            row.asset_class_id &&
            setEditingField({
              row,
              field: "target",
              anchorEl: e.currentTarget
            })
          }
        >
          {row.target
            ? row.target.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0
              })
            : "-"}
        </StyledInteractiveCell>
        <StyledInteractiveCell
          sx={{
            color: !isViewerPermission && row.asset_class_id ? "#26A6AE" : "#071F35",
            cursor: !isViewerPermission && row.asset_class_id ? "pointer" : "default"
          }}
          onClick={(e) =>
            !isViewerPermission &&
            row.asset_class_id &&
            setEditingField({
              row,
              field: "target_percent",
              anchorEl: e.currentTarget
            })
          }
        >
          {formatPercentage(row.target_percent)}
        </StyledInteractiveCell>
        <StyledBodyCell
          sx={{
            color: "#071F35"
          }}
        >
          {row.delta
            ? row.delta.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0
              })
            : "-"}
        </StyledBodyCell>
        <StyledBodyCell
          sx={{
            color: "#071F35"
          }}
        >
          {formatPercentage(row.delta_percentage)}
        </StyledBodyCell>

        <StyledActionsCell>
          {!isViewerPermission && row.asset_class_type === "custom" && (
            <IconButton
              onClick={() => handleDeleteAssetClass(row.asset_class_id)}
              sx={{
                minWidth: "auto",
                p: "4px",
                color: "#496682",
                opacity: 0,
                transition: "opacity 0.2s ease-in-out"
              }}
            >
              <DeleteIconOutline />
            </IconButton>
          )}
        </StyledActionsCell>
      </TableRow>
    );
  };

  const renderSpacerRow = (spacer: { isSpacer: true; title: string }, index: number) => (
    <TableRow
      key={`spacer-${index}`}
      sx={{
        backgroundColor: "#FFFFFF"
      }}
    >
      <StyledSpacerCell colSpan={8}>
        <Typography
          variant="body2"
          sx={{
            color: "#496682",
            textAlign: "center",
            width: "100%",
            fontSize: "14px",
            fontWeight: "400"
          }}
        >
          List of empty Asset Classes below
          <br />
          Select the item for adding it to the AI Construction
        </Typography>
      </StyledSpacerCell>
    </TableRow>
  );

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "white",
        flexGrow: 1,
        p: "16px",
        boxShadow: "0 1px 2px 0 #1212171a",
        borderRadius: "8px"
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#173c4c",
          fontSize: "16px",
          height: "auto",
          minHeight: "24px !important",
          backgroundColor: "white",
          maxHeight: "unset",
          px: "0 !important",
          mb: "22px"
        }}
      >
        <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="column" gap="2px">
            <Typography fontSize="18px" fontWeight="500" whiteSpace="nowrap">
              Asset Class Allocation
            </Typography>
            <Stack direction="row" alignItems="center" gap="4px">
              <Typography fontSize="13px" color="#496682">
                AUM
              </Typography>
              <Typography
                fontSize="13px"
                color={isViewerPermission ? "#071F35" : "#26A6AE"}
                sx={{
                  cursor: isViewerPermission ? "default" : "pointer",
                  ...(!isViewerPermission && { textDecoration: "underline", textDecorationStyle: "dotted" })
                }}
                onClick={(e) => {
                  if (isViewerPermission) return;
                  setAumInputValue(goalsData?.aum != null ? goalsData.aum.toString() : "");
                  setAumAnchorEl(e.currentTarget);
                }}
              >
                {formatAum(goalsData?.aum)}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" gap="8px">
            <FormControlLabel
              control={
                <Switch
                  checked={showEmptyClasses}
                  onChange={(e) => setShowEmptyClasses(e.target.checked)}
                  size="small"
                  sx={{
                    width: 48,
                    height: 24,
                    padding: 0,
                    "& .MuiSwitch-switchBase": {
                      padding: 0,
                      margin: "2px",
                      transitionDuration: "300ms",
                      color: "#bac7ce",
                      "&.Mui-checked": {
                        transform: "translateX(23px)",
                        color: "#0a3142",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "rgba(246, 248, 252, 1)",
                          opacity: 1,
                          border: 0
                        },
                        "&.Mui-disabled + .MuiSwitch-track": {
                          opacity: 0.5
                        }
                      },
                      "&.Mui-focusVisible .MuiSwitch-thumb": {
                        color: "#33cf4d",
                        border: "6px solid #fff"
                      },
                      "&.Mui-disabled .MuiSwitch-thumb": {
                        opacity: 0.7
                      },
                      "&.Mui-disabled + .MuiSwitch-track": {
                        opacity: 0.3
                      }
                    },
                    "& .MuiSwitch-thumb": {
                      boxSizing: "border-box",
                      width: 20,
                      height: 20
                    },
                    "& .MuiSwitch-track": {
                      borderRadius: "34px",
                      backgroundColor: "rgba(246, 248, 252, 1)",
                      opacity: 1,
                      transition: "background-color 500ms"
                    }
                  }}
                />
              }
              labelPlacement="start"
              label={
                <Typography fontSize="14px" color="#071F35">
                  Show Empty Asset Classes
                </Typography>
              }
              sx={{
                margin: 0,
                ".MuiSwitch-root": {
                  marginLeft: "8px"
                }
              }}
            />
            {!isViewerPermission && (
              <Button
                variant="outlined"
                color="primary"
                sx={{ height: "24px", minHeight: "24px" }}
                onClick={onAddAssetClass}
                loading={assetClassLoading}
              >
                Add Asset Class
              </Button>
            )}
          </Stack>
        </Stack>
      </Toolbar>

      <TableContainer
        sx={{
          height: "fit-content",
          maxHeight: "548px",
          overflowY: "auto",
          border: "1px solid #DBE8F5",
          borderRadius: "8px"
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>Asset Class</StyledHeaderCell>
              <StyledHeaderCell>Expected Return</StyledHeaderCell>
              <StyledHeaderCell>
                <Stack direction="row" alignItems="center" gap="6px">
                  Actual Investment
                  {isActualInvestmentOverAum && (
                    <MaterialTooltip isShow title="Sum of Actual Investments exceeds AUM">
                      <ErrorOutlineIcon sx={{ fontSize: "16px", color: "#d32f2f", cursor: "default" }} />
                    </MaterialTooltip>
                  )}
                </Stack>
              </StyledHeaderCell>
              <StyledHeaderCell>Actual Exposure</StyledHeaderCell>
              <StyledHeaderCell>Target</StyledHeaderCell>
              <StyledHeaderCell>Target Exposure</StyledHeaderCell>
              <StyledHeaderCell>Delta</StyledHeaderCell>
              <StyledHeaderCell>Delta %</StyledHeaderCell>
              <StyledHeaderCell sx={{ width: "50px" }}>{/* Actions column */}</StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <StyledBodyCell colSpan={8} sx={{ textAlign: "center", padding: "20px" }}>
                  <Typography>Loading...</Typography>
                </StyledBodyCell>
              </TableRow>
            ) : (
              splitData.map((row, index) => {
                if (isSpacerRow(row)) {
                  return showEmptyClasses && renderSpacerRow(row, index);
                } else {
                  return renderDataRow(row as IPortfolioConstructionTableRowData, index);
                }
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {editingField && (
        <EditTargetPopup
          anchorEl={editingField.anchorEl}
          onClose={() => setEditingField(null)}
          onSubmit={handleSubmit}
          state={editingField}
          onResetToOriginal={() => {
            deleteCustomActualInvestment({
              investor_id: investorId,
              asset_class_id: editingField.row.asset_class_id,
              asset_class_type: editingField.row.asset_class_type
            });
          }}
          loading={targetLoading || actualInvestmentLoading || updateExpectedReturnLoading || addExpectedReturnLoading}
        />
      )}
      <OpportunityPdfPreview
        opportunityData={rowOpportunity}
        onClose={() => setRowOpportunity([])}
        isOpen={!!rowOpportunity.length}
      />

      <Popover
        open={Boolean(aumAnchorEl)}
        anchorEl={aumAnchorEl}
        onClose={() => setAumAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Stack sx={{ p: "16px", minWidth: 260 }} gap="12px">
          <NumberInput
            label="AUM"
            value={aumInputValue}
            onChange={(e) => setAumInputValue(e.target.value)}
            size="small"
            numberInputProps={{
              allowNegative: false,
              isNumericString: true,
              decimalScale: 2
            }}
            InputProps={{
              startAdornment: <Typography sx={{ color: "#071F35", fontSize: "14px", marginRight: "8px" }}>$</Typography>
            }}
          />
        </Stack>
        <Stack direction="row" justifyContent="flex-end" gap="8px" sx={{ px: "16px", py: "10px" }}>
          <Button variant="text" sx={{ height: "28px", minHeight: "28px" }} onClick={() => setAumAnchorEl(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ height: "28px", minHeight: "28px" }}
            onClick={handleSaveAum}
            loading={saveAumLoading}
          >
            Save
          </Button>
        </Stack>
      </Popover>
    </Stack>
  );
};
